import axios, {parseAxiosPromise} from '@/axiosInstance'
import {z} from 'zod'
import {
    MailBatchSummary,
    MailLog,
    MailLogStatus,
    MailerRecipients,
    PreviewRecipientsResponse,
    SendMailResponse,
    TestSendResponse
} from '@/features/mailer/types'
import {captureException} from '@sentry/react'

// The mailer batches endpoints are inconsistent: the list endpoint returns pagination
// metadata in snake_case (per_page, current_page, last_page) while the detail endpoint
// uses camelCase (perPage, currentPage, lastPage). Normalize both shapes upfront so the
// rest of the app keeps working with the project-wide camelCase `PaginationResponse`.
const MailerPaginationMeta = z.preprocess(
    val => {
        if (typeof val !== 'object' || val === null) return val
        const v = val as Record<string, unknown>
        return {
            total: v.total,
            perPage: v.perPage ?? v.per_page,
            currentPage: v.currentPage ?? v.current_page,
            lastPage: v.lastPage ?? v.last_page
        }
    },
    z.object({
        total: z.number(),
        perPage: z.number(),
        currentPage: z.number(),
        lastPage: z.number()
    })
)

export const MailBatchListResponse = z.object({
    data: z.array(MailBatchSummary),
    meta: MailerPaginationMeta
})
export type MailBatchListResponse = z.infer<typeof MailBatchListResponse>

export const MailBatchDetailResponse = z.object({
    data: z.array(MailLog),
    meta: MailerPaginationMeta
})
export type MailBatchDetailResponse = z.infer<typeof MailBatchDetailResponse>

// --- Recipients preview (dry-run counts + sample, OPSSTD-3643 §1) ---

export const httpPreviewRecipients = (recipients: MailerRecipients, signal?: AbortSignal) =>
    parseAxiosPromise({
        axiosPromise: axios.post('/mailer/recipients/preview', {recipients}, {signal}),
        responseShape: PreviewRecipientsResponse,
        onZodError: captureException
    })

// --- Test send to the logged operator (OPSSTD-3643 §2) ---

export interface HttpSendTestMailOptions {
    subject: string
    body: string
    attachments?: File[]
}
export const httpSendTestMail = ({subject, body, attachments}: HttpSendTestMailOptions) => {
    const formData = new FormData()
    formData.append('subject', subject)
    formData.append('body', body)
    if (attachments?.length) {
        attachments.forEach(file => {
            formData.append('attachments', file)
        })
    }

    return parseAxiosPromise({
        axiosPromise: axios.post('/mailer/test', formData, {
            headers: {'Content-Type': 'multipart/form-data'}
        }),
        responseShape: TestSendResponse,
        onZodError: captureException
    })
}

// --- Real send (OPSSTD-3643 §3) ---

export interface HttpSendMailOptions {
    subject: string
    body: string
    recipients: MailerRecipients
    attachments?: File[]
}
export const httpSendMail = ({subject, body, recipients, attachments}: HttpSendMailOptions) => {
    const formData = new FormData()
    formData.append('subject', subject)
    formData.append('body', body)
    formData.append('recipients', JSON.stringify(recipients))
    if (attachments?.length) {
        attachments.forEach(file => {
            formData.append('attachments', file)
        })
    }

    return parseAxiosPromise({
        axiosPromise: axios.post('/mailer/send', formData, {
            headers: {'Content-Type': 'multipart/form-data'}
        }),
        responseShape: SendMailResponse,
        onZodError: captureException
    })
}

// --- History ---

export interface HttpGetMailBatchesParams {
    page: number
    limit: number
    search?: string
}
export const httpGetMailBatches = (params: HttpGetMailBatchesParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get('/mailer/batches', {params}),
        responseShape: MailBatchListResponse,
        onZodError: captureException
    })

export interface HttpGetMailBatchDetailParams {
    batchId: string
    page: number
    limit: number
    status?: MailLogStatus
}
export const httpGetMailBatchDetail = ({batchId, ...params}: HttpGetMailBatchDetailParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get(`/mailer/batches/${batchId}`, {params}),
        responseShape: MailBatchDetailResponse,
        onZodError: captureException
    })
